// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import { SyntheticEvent, useContext, useEffect, useState } from 'react';
import { useRouter } from 'next/router';

import { apiPath, dashboardCalendarsPath } from 'presentations/routes/path';
import { moveFromOrDefaultPath } from 'presentations/views/helpers/router';
import { useRequest } from 'presentations/views/hooks/request';
import { UserProfileItem, ViewerItem } from 'presentations/api/schema/user';
import { patch } from 'infras/rest_client';
import {
  DashboardPortalContext,
  setErrorMessage,
  setSuccessMessage,
} from 'presentations/reducers/DashboardPortal';
import { defaultStartWeekDay } from 'domains/models/event_type/startWeekDay';
import StartWeekDayFeature from 'domains/models/subscription/features/start_weekday';

export interface Props {
  viewer: ViewerItem;
}

export function useDashboardCalendarSettingPage(props: Props) {
  const t = translateInComponent(translations);

  const navigationContext = useContext(DashboardPortalContext);
  const router = useRouter();

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [startWeekDay, setStartWeekDay] = useState<number>(defaultStartWeekDay);

  const userProfileRequest = useRequest(apiPath('/v1/user/profile'), {
    revalidateOnFocus: false,
  });
  const userProfile: UserProfileItem | null = userProfileRequest.data;

  const startWeekDayFeature = new StartWeekDayFeature({
    planVersion: props.viewer.planVersion,
  });
  const isValidStartWeekDay = startWeekDayFeature.isValid(props.viewer.planId);

  useEffect(() => {
    if (!isValidStartWeekDay) return;
    setStartWeekDay(userProfile?.startWeekDay ?? defaultStartWeekDay);
  }, [userProfile]);

  const canGoBack = router.isReady && typeof router.query.from === 'string';
  const onClickBack = () => {
    if (typeof router.query.from === 'string') {
      moveFromOrDefaultPath(router, dashboardCalendarsPath());
    }
  };

  const onChange = (
    e: SyntheticEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.currentTarget;
    switch (name) {
      case 'startWeekDay':
        setStartWeekDay(Number(value));
        break;
      default:
        break;
    }
  };

  const onChangeStartWeekDay = (wday: number) => {
    if (!isValidStartWeekDay) return;

    setStartWeekDay(wday);
  };

  const onSubmit = async () => {
    setIsLoading(true);
    const result = await patch(apiPath('/v1/user/update'), {
      data: { startWeekDay },
    });
    if (!result.isSuccess) {
      setIsLoading(false);
      navigationContext.dispatch(setErrorMessage(t('failed to update')));
      return;
    }
    setIsLoading(false);
    navigationContext.dispatch(setSuccessMessage(t('success to update')));

    if (typeof router.query.from === 'string') {
      moveFromOrDefaultPath(router, dashboardCalendarsPath());
    }
  };

  return {
    state: {
      isInitLoading: userProfileRequest.isLoading,
      isLoading,
      canGoBack,
      startWeekDay,
      isValidStartWeekDay,
    },
    actions: { onClickBack, onChange, onChangeStartWeekDay, onSubmit },
  };
}
